<template>
	<div :class="classNames" @click="$emit('click', $event)">
		<div class="country-list-item__container">
			<a>
				<img class="country-list-item__flag" :src="src" :alt="webLocale" />
				<span class="country-list-item__text">
					{{ $t(`WEBAPP_COUNTRY_${webLocale.toUpperCase()}`) }}
				</span>
			</a>
			<FontAwesomeIcon v-if="selected || excluded" class="country-list-item__icon" :icon="icon" />
		</div>
	</div>
</template>

<script setup lang="ts">
import type { WebLocale } from '@/enums/web-locale';

import { computed } from 'vue';
import { faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import { jwCheckIcon } from '@/plugins/global-fontawesome';

const props = withDefaults(
	defineProps<{
		webLocale: WebLocale;
		selected?: boolean;
		excluded?: boolean;
	}>(),
	{
		selected: false,
		excluded: false,
	}
);

const classNames = computed(() => {
	return {
		'country-list-item': true,
		'country-list-item--selected': props.selected,
		'country-list-item--excluded': props.excluded,
	};
});

const icon = computed(() => {
	if (props.excluded) {
		return faMinusCircle;
	}

	if (props.selected) {
		return jwCheckIcon;
	}
	return undefined;
});

const { jwConfig } = useRuntimeConfig().public;
const src = computed(() => `${jwConfig.ASSETS_URL}/flags/${props.webLocale.toUpperCase()}.png`);
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';
@import '~@/styles/rtl-support-mixins';

$transition-time: 0.3s;

.country-list-item {
	transition: background-color $transition-time, color $transition-time ease-in;
	font-size: 14px;
	text-align: start;
	cursor: pointer;

	&__container {
		position: relative;
		padding: 10px 0;

		@media (min-width: 480px) {
			padding: 5px 0;
		}

		.country-list__highlighted & {
			border: none;
		}
	}

	& #{&}__flag {
		// Double selector here to fight off slider styles.
		height: 32px;
		width: 32px;
		margin-inline-end: 5px;
	}

	&__text {
		.country-list-item--selected & {
			color: $jw-color-lightgray;
		}
	}

	&__icon {
		position: absolute;
		@include inset-inline('end', 11px);
		top: 18px;
	}

	&--selected,
	&--excluded {
		@include full-width();
		background-color: rgba($jw-color-lightblue, 0.2);
		padding-left: 15px;
		padding-right: 15px;
	}
}

.country-list__highlighted {
	font-size: 20px;

	.country-list-item__text {
		color: $jw-color-lightgray !important;
	}
}
</style>
